import {
  selfImportFieldsDe,
  selfImportFieldsEn,
  selfImportFieldsFr,
  selfImportFieldsEs,
  selfImportFieldsTr,
  selfImportFieldsRu,
} from './self-import-fields-i18n';

const entitiesEn = {
  CUSTOMER: 'Customers',
  SUPPLIER: 'Suppliers',
  PROSPECT: 'Prospects',
  CONTACT: 'Contacts',
  ADDRESS: 'Addresses and shipping addresses',
  PRODUCT: 'Products',
  PRODUCT_CATEGORY: 'Product categories',
  PRODUCT_PACKAGING: 'Packaging units',
  PRODUCT_PRICE: 'Prices and tier prices',
  SUPPLIER_PRODUCT: 'Supplier product mappings',
  SUPPLIER_PRICE: 'Supplier purchase price tiers',
  INITIAL_STOCK: 'Initial stock',
  BATCH: 'Batches and expiry dates',
  OPEN_ITEM: 'Open items',
  CUSTOMER_PRICE: 'Customer-specific prices',
  CUSTOM_FIELD_VALUE: 'Custom-field values',
  STOCK_ADJUSTMENT: 'Legacy stock adjustments',
};

const fieldsEn = {
  ...selfImportFieldsEn,
  industryCode: 'Industry code',
  externalId: 'External source ID',
  customerNumber: 'Customer/supplier number',
  companyName: 'Company/name',
  email: 'Email',
  phone: 'Phone',
  street: 'Street',
  houseNumber: 'House number',
  postalCode: 'Postal code',
  city: 'City',
  countryCode: 'Country',
  legalFormCode: 'Legal form code',
  legalFormName: 'Other legal form name',
  legalFormJurisdiction: 'Legal jurisdiction',
  responsibilities: 'Responsibilities (JSON array)',
  subdivisionCode: 'Département code',
  subdivisionName: 'Département name',
  region: 'Region',
  vatId: 'VAT ID',
  iban: 'IBAN',
  partnerExternalId: 'Partner external ID',
  firstName: 'First name',
  lastName: 'Last name',
  position: 'Position',
  label: 'Address label',
  addressType: 'Address type',
  isDefaultShipping: 'Default shipping address',
  articleNumber: 'Article number',
  name: 'Name',
  description: 'Description',
  ean: 'GTIN/EAN',
  productGroup: 'Product group',
  baseUnit: 'Base unit',
  listPrice: 'Sales price',
  purchasePrice: 'Purchase price',
  taxRateType: 'Tax rate',
  minStock: 'Minimum stock',
  slug: 'Technical key',
  parent: 'Parent category',
  code: 'Packaging code',
  quantity: 'Quantity',
  unit: 'Unit',
  priceGroup: 'Price list/group',
  currency: 'Currency',
  preferredLanguage: 'Document language',
  minQuantity: 'Minimum quantity',
  unitPrice: 'Unit price',
  supplierArticleNumber: 'Supplier article number',
  leadTimeDays: 'Lead time in days',
  warehouseCode: 'Warehouse',
  reason: 'Reason',
  batchNumber: 'Batch number',
  expiryDate: 'Best-before/expiry date',
  productionDate: 'Production date',
  documentNumber: 'Document number',
  documentDate: 'Document date',
  dueDate: 'Due date',
  grossAmount: 'Gross amount',
  paidAmount: 'Paid amount',
  entityType: 'Entity type',
  recordKey: 'Record key',
  fieldKey: 'Custom-field key',
  value: 'Value',
};

const statusesEn = {
  PENDING: 'Pending',
  MAPPING: 'Mapping',
  VALIDATED: 'Validated',
  QUEUED: 'Queued',
  PROCESSING: 'Processing',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
  ROLLED_BACK: 'Rolled back',
};
const fieldTypesEn = {
  TEXT: 'Text',
  TEXTAREA: 'Long text',
  NUMBER: 'Number',
  DECIMAL: 'Decimal number',
  CHECKBOX: 'Checkbox',
  MULTI_SELECT: 'Multiple selection',
  DATE: 'Date',
  BOOLEAN: 'Checkbox',
  SELECT: 'Selection',
};
const flowEn = {
  1: 'Upload file',
  2: 'Detect format',
  3: 'Select sheet',
  4: 'Detect header',
  5: 'Select target',
  6: 'Map columns',
  7: 'Transform values',
  8: 'Preview',
  9: 'Validate',
  10: 'Resolve duplicates',
  11: 'Dry run',
  12: 'Import',
  13: 'Report',
};

const base = {
  referenceMissing: 'The unique master or source reference is missing.',
  migrationIssueUnreadable:
    'The stored issue cannot be read. Have the original evidence checked before continuing.',
  migrationIssueTitle: 'Migration needs review',
  migrationIssueHint:
    'Review the evidence for the affected source rows or the whole run. Continue only through the controlled migration process after resolving the cause.',
  migrationIssueJob: 'Whole migration run',
  migrationIssueRow: 'Source row {{row}}',
  migrationEntityLabel: 'Historical migration',

  emptyCells: 'Empty cells',
  industryCodeHint:
    'Import industry codes as text. When spreadsheet values are already numeric, lost leading zeros cannot be reliably reconstructed. Check the preview; no zeros are added.',
  emptyKeep: 'Empty cell: keep existing value',
  emptyClear: 'Empty cell: clear value',

  title: 'Import center',
  subtitle: 'Validated, resumable transfer of master data from CSV and XLSX.',
  flowSteps: flowEn,
  uploadTitle: '1–2 · Upload and file detection',
  uploadHint: 'The protected original is stored with SHA-256 in the DMS.',
  dropHere: 'Drop CSV or XLSX here',
  formats: 'CSV and XLSX up to 50 MB; UTF-8, UTF-16 and Windows-1252.',
  chooseFile: 'Choose file',
  encoding: 'Encoding',
  delimiter: 'Delimiter',
  sourceConfiguration: '3–5 · Source and target',
  sourceConfigurationHint: 'Review worksheet, detected header row and target entity.',
  sheet: 'Worksheet',
  headerRow: 'Header row',
  target: 'Target entity',
  confirmSource: 'Read selected table',
  mappingTitle: '6–10 · Mapping, transformation and duplicates',
  mappingHint: '{{rows}} data rows; {{ignored}} empty or total rows ignored.',
  requiredOnCreate: 'required for new records',
  productUpdateHint:
    'Choose Update to match existing products by article number. Only mapped values change; empty cells keep existing values by default. New products require a name.',
  sourceColumn: 'Source column',
  sample: 'Sample values',
  targetField: 'ERP field',
  ignore: 'Ignore',
  transform: 'Transformation',
  none: 'None',
  newCustomField: 'Create custom field',
  localeFormat: 'Number/date format',
  countryMap: 'Country mapping (source=ISO)',
  unitMap: 'Unit mapping (source=code)',
  taxMap: 'Tax mapping (source=code)',
  countryMapExample: 'Germany=DE',
  unitMapExample: 'Piece=PCE',
  taxMapExample: '19%=STANDARD',
  duplicateStrategy: 'Duplicate strategy',
  runDryRun: 'Run dry run',
  customProposalTitle: 'New custom field for “{{column}}”',
  customProposalHint: 'Names, descriptions and category must be reviewed in every ERP language.',
  technicalKey: 'Technical key',
  fieldType: 'Field type',
  category: 'Category',
  createCategory: 'Create controlled category',
  categoryKey: 'Category key',
  name: 'Name',
  description: 'Description/help',
  categoryName: 'Category name',
  categoryDescription: 'Category description',
  validationTitle: '11 · Preview and dry-run result',
  validationHint: 'No production data has been changed.',
  validRows: 'Valid rows',
  invalidRows: 'Invalid rows',
  duplicatesFound: 'Duplicates',
  willCreate: 'Will create',
  willUpdate: 'Will update',
  row: 'Row',
  field: 'Field',
  reason: 'Reason',
  manualDecisions: 'Manual duplicate decisions',
  bulkUpdateUnambiguous: 'Update all unambiguously matched records',
  bulkUpdateUnambiguousHint:
    'Applies to the entire import, including rows outside this preview. Individual decisions are retained. Multiple matches still require a decision. Run the dry run again before confirming.',
  dryRunOutdated:
    'The displayed result is no longer current. Review your decisions and run the dry run again before confirming the import.',
  manualDecisionProgress:
    'Showing {{shown}} of {{total}} pending decisions from the last dry run. Choose the actions for these rows, then run the dry run again to see any remaining decisions.',
  chooseDecision: 'Choose decision',
  explicitConfirmation: 'I reviewed the preview and explicitly approve the productive import.',
  allowPartial: 'Import valid rows despite {{count}} invalid rows.',
  startImport: 'Import {{count}} rows',
  importAccepted: 'Import was accepted and can be resumed safely.',
  resultTitle: '12–13 · Progress and result report',
  created: 'created',
  updated: 'updated',
  skipped: 'skipped',
  errorReport: 'Download corrected error file',
  resume: 'Resume',
  newImport: 'New import',
  sourceFile: 'Protected source file',
  history: 'Import history',
  historyHint: 'Hash, user, mapping result and protected reports remain traceable.',
  settingsTitle: 'Import retention and processing',
  settingsHint: 'Configure source-file retention and the row threshold for queued jobs.',
  retentionDays: 'Source-file retention (days)',
  largeJobThreshold: 'Queue from this row count',
  settingsSaved: 'Import settings saved.',
  noJobs: 'No imports yet.',
  file: 'File',
  progress: 'Progress',
  status: 'Status',
  executedAt: 'Completed',
  rowSummary: '{{success}} successful · {{failed}} errors · {{total}} total',
  profileTitle: 'Reusable migration profile',
  profileHint: 'Apply a versioned mapping or save the current reviewed configuration.',
  profileReviewWarning:
    'System profiles identify the source only. They never assume a vendor layout; review every mapping before the dry run.',
  profile: 'Profile',
  sourceSystem: 'Source system',
  newProfileName: 'Name for a new custom profile',
  saveProfile: 'Save mapping as profile',
  updateProfile: 'Save new profile version',
  archiveProfile: 'Archive profile',
  profileSaved: 'Import profile saved.',
  profileUpdated: 'A new profile version was saved.',
  profileArchived: 'Import profile archived.',
  profiles: {
    generic: 'Generic CSV/XLSX',
    orgamax: 'orgaMAX',
    odoo: 'Odoo',
    logoTiger: 'LOGO/Tiger',
  },
  openHistory: 'Open complete import history',
  historyDetails: 'Import run details',
  historyDetailsHint: 'Source, parameters, affected records, audit trail and rollback checks.',
  mappingVersion: 'Mapping version',
  dryRunAt: 'Dry run',
  auditEntries: 'Audit entries',
  vaultEntries: 'GoBD vault entries',
  mappingParameters: 'Exact import parameters',
  operation: 'Operation',
  affectedRecord: 'Affected record',
  historyPage: 'Page {{page}} of {{pages}} · {{total}} rows',
  rollbackTitle: 'Safe rollback',
  rollbackHint:
    'Only unchanged records created solely by this import and without downstream use can be deleted automatically.',
  rollbackPermissionRequired: 'The exceptional rollback permission is required.',
  checkRollback: 'Check rollback eligibility',
  correctionList: 'Download correction list',
  rollbackReason: 'Mandatory reason',
  rollbackConfirmation:
    'I reviewed the check and explicitly confirm deletion of the listed import-created records.',
  executeRollback: 'Execute confirmed rollback',
  rollbackCompleted: 'The import was rolled back and fully audited.',
  rollbackBlocked: 'The data changed; a correction list was generated instead.',
  rollbackStatuses: { ELIGIBLE: 'Eligible', BLOCKED: 'Blocked', COMPLETED: 'Completed' },
  entities: entitiesEn,
  statuses: statusesEn,
  fields: fieldsEn,
  fieldTypes: fieldTypesEn,
  transformSearch: 'Find value',
  transformReplacement: 'Replacement',
  transformValues: 'One mapping per line: source=target',
  transformDefault: 'Default for empty values',
  transforms: {
    TRIM: 'Trim whitespace',
    UPPERCASE: 'Uppercase',
    LOWERCASE: 'Lowercase',
    REPLACE: 'Replace text',
    VALUE_MAP: 'Map values',
    DEFAULT: 'Set default',
  },
  localeFormats: {
    AUTO: 'Detect automatically',
    DE: 'German',
    FR: 'French',
    INTERNATIONAL: 'International (ISO/US)',
  },
  duplicates: {
    SKIP: 'Skip existing record',
    UPDATE: 'Update existing record',
    CREATE_NEW: 'Create a new record',
    MANUAL: 'Decide manually',
  },
};

export const importCenterEn = base;

export const importCenterDe = {
  ...base,
  referenceMissing: 'Die eindeutige Stamm- oder Quellenzuordnung fehlt.',
  migrationIssueUnreadable:
    'Der gespeicherte Hinweis ist nicht lesbar. Lassen Sie vor der Fortsetzung den Originalnachweis prüfen.',
  migrationIssueTitle: 'Übernahme muss geprüft werden',
  migrationIssueHint:
    'Prüfen Sie die Nachweise zu den betroffenen Quellzeilen oder zum gesamten Lauf. Setzen Sie die Übernahme nach Klärung der Ursache nur über den kontrollierten Migrationsablauf fort.',
  migrationIssueJob: 'Gesamter Übernahmelauf',
  migrationIssueRow: 'Quellzeile {{row}}',
  migrationEntityLabel: 'Historische Datenübernahme',

  emptyCells: 'Leere Zellen',
  industryCodeHint:
    'Branchencodes als Text importieren. Bei bereits numerisch gelieferten Tabellenwerten können verlorene führende Nullen nicht zuverlässig rekonstruiert werden. Prüfen Sie die Vorschau; es werden keine Nullen ergänzt.',
  emptyKeep: 'Leere Zelle: vorhandenen Wert beibehalten',
  emptyClear: 'Leere Zelle: Wert ausdrücklich löschen',
  title: 'Import-Center',
  subtitle: 'Geprüfte, fortsetzbare Übernahme von Stammdaten aus CSV und XLSX.',
  flowSteps: {
    1: 'Datei hochladen',
    2: 'Format erkennen',
    3: 'Blatt wählen',
    4: 'Kopfzeile erkennen',
    5: 'Ziel wählen',
    6: 'Spalten zuordnen',
    7: 'Werte umformen',
    8: 'Vorschau',
    9: 'Validieren',
    10: 'Dubletten klären',
    11: 'Testlauf',
    12: 'Importieren',
    13: 'Bericht',
  },
  uploadTitle: '1–2 · Upload und Dateierkennung',
  uploadHint: 'Das geschützte Original wird mit SHA-256 im DMS abgelegt.',
  dropHere: 'CSV oder XLSX hier ablegen',
  formats: 'CSV und XLSX bis 50 MB; UTF-8, UTF-16 und Windows-1252.',
  chooseFile: 'Datei auswählen',
  encoding: 'Kodierung',
  delimiter: 'Trennzeichen',
  sourceConfiguration: '3–5 · Quelle und Ziel',
  sourceConfigurationHint: 'Tabellenblatt, erkannte Kopfzeile und Zielentität prüfen.',
  sheet: 'Tabellenblatt',
  headerRow: 'Kopfzeile',
  target: 'Zielentität',
  confirmSource: 'Tabelle einlesen',
  mappingTitle: '6–10 · Zuordnung, Umformung und Dubletten',
  mappingHint: '{{rows}} Datenzeilen; {{ignored}} Leer- oder Summenzeilen ignoriert.',
  requiredOnCreate: 'nur bei Neuanlage erforderlich',
  productUpdateHint:
    'Wählen Sie Aktualisieren, um bestehende Artikel anhand der Artikelnummer zuzuordnen. Nur zugeordnete Werte ändern sich; leere Zellen behalten standardmäßig bestehende Werte bei. Neue Artikel benötigen einen Namen.',
  sourceColumn: 'Quellspalte',
  sample: 'Beispielwerte',
  targetField: 'ERP-Feld',
  ignore: 'Nicht übernehmen',
  transform: 'Umformung',
  none: 'Keine',
  newCustomField: 'Als Zusatzfeld anlegen',
  localeFormat: 'Zahlen-/Datumsformat',
  countryMap: 'Länderzuordnung (Quelle=ISO)',
  unitMap: 'Einheitenzuordnung (Quelle=Code)',
  taxMap: 'Steuerzuordnung (Quelle=Code)',
  duplicateStrategy: 'Dublettenstrategie',
  runDryRun: 'Testlauf starten',
  customProposalTitle: 'Neues Zusatzfeld für „{{column}}“',
  customProposalHint:
    'Namen, Beschreibungen und Kategorie müssen in jeder ERP-Sprache geprüft werden.',
  technicalKey: 'Technischer Schlüssel',
  fieldType: 'Feldtyp',
  category: 'Kategorie',
  createCategory: 'Kategorie kontrolliert anlegen',
  categoryKey: 'Kategorieschlüssel',
  name: 'Name',
  description: 'Beschreibung/Hilfe',
  categoryName: 'Kategoriename',
  categoryDescription: 'Kategoriebeschreibung',
  validationTitle: '11 · Vorschau und Testlaufergebnis',
  validationHint: 'Es wurden noch keine Produktivdaten verändert.',
  validRows: 'Gültige Zeilen',
  invalidRows: 'Fehlerhafte Zeilen',
  duplicatesFound: 'Dubletten',
  willCreate: 'Wird angelegt',
  willUpdate: 'Wird aktualisiert',
  row: 'Zeile',
  field: 'Feld',
  reason: 'Grund',
  manualDecisions: 'Manuelle Dublettenentscheidungen',
  bulkUpdateUnambiguous: 'Alle eindeutig zugeordneten Einträge aktualisieren',
  bulkUpdateUnambiguousHint:
    'Gilt für den gesamten Import, auch außerhalb dieser Vorschau. Einzelentscheidungen bleiben erhalten. Mehrdeutige Treffer verlangen weiterhin eine Entscheidung. Starten Sie vor der Bestätigung einen neuen Testlauf.',
  dryRunOutdated:
    'Das angezeigte Ergebnis ist nicht mehr aktuell. Prüfen Sie Ihre Entscheidungen und starten Sie vor der Importbestätigung einen neuen Testlauf.',
  manualDecisionProgress:
    'Angezeigt werden {{shown}} von {{total}} offenen Entscheidungen aus dem letzten Testlauf. Wählen Sie die Aktionen für diese Zeilen und starten Sie danach den Testlauf erneut, um weitere offene Entscheidungen anzuzeigen.',
  chooseDecision: 'Entscheidung wählen',
  explicitConfirmation:
    'Ich habe die Vorschau geprüft und gebe den produktiven Import ausdrücklich frei.',
  allowPartial: 'Gültige Zeilen trotz {{count}} fehlerhafter Zeilen importieren.',
  startImport: '{{count}} Zeilen importieren',
  importAccepted: 'Import angenommen und sicher fortsetzbar.',
  resultTitle: '12–13 · Fortschritt und Ergebnisbericht',
  created: 'angelegt',
  updated: 'aktualisiert',
  skipped: 'übersprungen',
  errorReport: 'Korrigierbare Fehlerdatei laden',
  resume: 'Fortsetzen',
  newImport: 'Neuer Import',
  sourceFile: 'Geschützte Quelldatei',
  history: 'Importhistorie',
  historyHint: 'Hash, Benutzer, Mappingergebnis und geschützte Berichte bleiben nachvollziehbar.',
  settingsTitle: 'Aufbewahrung und Verarbeitung',
  settingsHint:
    'Aufbewahrungsfrist der Quelldateien und Schwelle für Warteschlangenjobs festlegen.',
  retentionDays: 'Quelldateien aufbewahren (Tage)',
  largeJobThreshold: 'Ab dieser Zeilenzahl einreihen',
  settingsSaved: 'Importeinstellungen gespeichert.',
  noJobs: 'Noch keine Importe.',
  file: 'Datei',
  progress: 'Fortschritt',
  status: 'Status',
  executedAt: 'Abgeschlossen',
  rowSummary: '{{success}} erfolgreich · {{failed}} Fehler · {{total}} gesamt',
  entities: {
    CUSTOMER: 'Kunden',
    SUPPLIER: 'Lieferanten',
    PROSPECT: 'Interessenten',
    CONTACT: 'Ansprechpartner',
    ADDRESS: 'Adressen und Lieferadressen',
    PRODUCT: 'Artikel',
    PRODUCT_CATEGORY: 'Produktkategorien',
    PRODUCT_PACKAGING: 'Verpackungseinheiten',
    PRODUCT_PRICE: 'Preise und Staffelpreise',
    SUPPLIER_PRODUCT: 'Lieferanten-Artikelzuordnungen',
    SUPPLIER_PRICE: 'Lieferanten-Einkaufspreisstaffeln',
    INITIAL_STOCK: 'Anfangsbestände',
    BATCH: 'Chargen und MHD',
    OPEN_ITEM: 'Offene Posten',
    CUSTOMER_PRICE: 'Kundenspezifische Preise',
    CUSTOM_FIELD_VALUE: 'Zusatzfeldwerte',
    STOCK_ADJUSTMENT: 'Alte Bestandskorrekturen',
  },
  statuses: {
    PENDING: 'Wartet',
    MAPPING: 'Zuordnung',
    VALIDATED: 'Geprüft',
    QUEUED: 'Eingereiht',
    PROCESSING: 'Läuft',
    COMPLETED: 'Abgeschlossen',
    FAILED: 'Fehlgeschlagen',
    CANCELLED: 'Abgebrochen',
    ROLLED_BACK: 'Zurückgenommen',
  },
  fields: {
    ...fieldsEn,
    ...selfImportFieldsDe,
    industryCode: 'Branchencode',
    externalId: 'Externe Quell-ID',
    customerNumber: 'Kunden-/Lieferantennummer',
    companyName: 'Firma/Name',
    phone: 'Telefon',
    street: 'Straße',
    houseNumber: 'Hausnummer',
    postalCode: 'PLZ',
    city: 'Ort',
    countryCode: 'Land',
    subdivisionCode: 'Départementcode',
    subdivisionName: 'Départementname',
    region: 'Region',
    legalFormCode: 'Rechtsformcode',
    legalFormName: 'Andere Rechtsform: Bezeichnung',
    legalFormJurisdiction: 'Rechtsgebiet',
    responsibilities: 'Zuständigkeiten (JSON-Array)',
    vatId: 'USt-ID',
    partnerExternalId: 'Externe Partner-ID',
    firstName: 'Vorname',
    lastName: 'Nachname',
    position: 'Position',
    label: 'Adressbezeichnung',
    addressType: 'Adresstyp',
    isDefaultShipping: 'Standard-Lieferadresse',
    articleNumber: 'Artikelnummer',
    name: 'Bezeichnung',
    description: 'Beschreibung',
    productGroup: 'Warengruppe',
    baseUnit: 'Basiseinheit',
    listPrice: 'Verkaufspreis',
    purchasePrice: 'Einkaufspreis',
    taxRateType: 'Steuersatz',
    minStock: 'Meldebestand',
    slug: 'Technischer Schlüssel',
    parent: 'Oberkategorie',
    code: 'Verpackungscode',
    quantity: 'Menge',
    unit: 'Einheit',
    priceGroup: 'Preisliste/-gruppe',
    currency: 'Währung',
    preferredLanguage: 'Belegsprache',
    minQuantity: 'Mindestmenge',
    unitPrice: 'Stückpreis',
    supplierArticleNumber: 'Lieferantenartikelnummer',
    leadTimeDays: 'Lieferzeit in Tagen',
    warehouseCode: 'Lager',
    reason: 'Grund',
    batchNumber: 'Chargennummer',
    expiryDate: 'MHD/Ablaufdatum',
    productionDate: 'Produktionsdatum',
    documentNumber: 'Belegnummer',
    documentDate: 'Belegdatum',
    dueDate: 'Fälligkeitsdatum',
    grossAmount: 'Bruttobetrag',
    paidAmount: 'Bezahlter Betrag',
    entityType: 'Entität',
    recordKey: 'Datensatzschlüssel',
    fieldKey: 'Zusatzfeldschlüssel',
    value: 'Wert',
  },
  fieldTypes: {
    TEXT: 'Text',
    TEXTAREA: 'Langtext',
    NUMBER: 'Zahl',
    DECIMAL: 'Dezimalzahl',
    CHECKBOX: 'Kontrollkästchen',
    MULTI_SELECT: 'Mehrfachauswahl',
    DATE: 'Datum',
    BOOLEAN: 'Checkbox',
    SELECT: 'Auswahl',
  },
  transformSearch: 'Suchwert',
  transformReplacement: 'Ersetzung',
  transformValues: 'Eine Zuordnung je Zeile: Quelle=Ziel',
  transformDefault: 'Standard für leere Werte',
  transforms: {
    TRIM: 'Leerzeichen entfernen',
    UPPERCASE: 'Großschreibung',
    LOWERCASE: 'Kleinschreibung',
    REPLACE: 'Text ersetzen',
    VALUE_MAP: 'Werte zuordnen',
    DEFAULT: 'Standard setzen',
  },
  localeFormats: {
    AUTO: 'Automatisch erkennen',
    DE: 'Deutsch',
    FR: 'Französisch',
    INTERNATIONAL: 'International (ISO/US)',
  },
  duplicates: {
    SKIP: 'Bestehenden Datensatz überspringen',
    UPDATE: 'Bestehenden Datensatz aktualisieren',
    CREATE_NEW: 'Neuen Datensatz erzeugen',
    MANUAL: 'Manuell entscheiden',
  },
  profileTitle: 'Wiederverwendbares Migrationsprofil',
  countryMapExample: 'Deutschland=DE',
  unitMapExample: 'Stück=PCE',
  taxMapExample: '19%=STANDARD',
  profileHint: 'Versionierte Zuordnung anwenden oder die aktuell geprüfte Konfiguration speichern.',
  profileReviewWarning:
    'Systemprofile kennzeichnen nur die Quelle. Sie unterstellen kein Fremdformat; jede Zuordnung ist vor dem Testlauf zu prüfen.',
  profile: 'Profil',
  sourceSystem: 'Quellsystem',
  newProfileName: 'Name für ein neues Kundenprofil',
  saveProfile: 'Zuordnung als Profil speichern',
  updateProfile: 'Neue Profilversion speichern',
  archiveProfile: 'Profil archivieren',
  profileSaved: 'Importprofil gespeichert.',
  profileUpdated: 'Eine neue Profilversion wurde gespeichert.',
  profileArchived: 'Importprofil archiviert.',
  profiles: {
    generic: 'Generisch CSV/XLSX',
    orgamax: 'orgaMAX',
    odoo: 'Odoo',
    logoTiger: 'LOGO/Tiger',
  },
  openHistory: 'Vollständige Importhistorie öffnen',
  historyDetails: 'Details des Importlaufs',
  historyDetailsHint:
    'Quelle, Parameter, betroffene Datensätze, Audit-Trail und Rollback-Prüfungen.',
  mappingVersion: 'Mappingversion',
  dryRunAt: 'Testlauf',
  auditEntries: 'Audit-Einträge',
  vaultEntries: 'GoBD-Vault-Einträge',
  mappingParameters: 'Exakte Importparameter',
  operation: 'Vorgang',
  affectedRecord: 'Betroffener Datensatz',
  historyPage: 'Seite {{page}} von {{pages}} · {{total}} Zeilen',
  rollbackTitle: 'Sicherer Rollback',
  rollbackHint:
    'Automatisch gelöscht werden nur unveränderte, ausschließlich durch diesen Import erzeugte Datensätze ohne Folgebezug.',
  rollbackPermissionRequired: 'Dafür ist die Sonderberechtigung für Import-Rollbacks erforderlich.',
  checkRollback: 'Rollback-Fähigkeit prüfen',
  correctionList: 'Korrekturliste herunterladen',
  rollbackReason: 'Pflichtbegründung',
  rollbackConfirmation:
    'Ich habe die Prüfung kontrolliert und bestätige ausdrücklich die Löschung der aufgeführten Importdatensätze.',
  executeRollback: 'Bestätigten Rollback ausführen',
  rollbackCompleted: 'Der Import wurde vollständig protokolliert zurückgesetzt.',
  rollbackBlocked:
    'Der Datenstand hat sich geändert; stattdessen wurde eine Korrekturliste erzeugt.',
  rollbackStatuses: { ELIGIBLE: 'Zulässig', BLOCKED: 'Blockiert', COMPLETED: 'Abgeschlossen' },
};

export const importCenterFr = {
  ...base,
  referenceMissing: 'La référence unique de fiche ou de source manque.',
  migrationIssueUnreadable:
    'L’anomalie enregistrée est illisible. Faites vérifier le justificatif original avant de continuer.',
  migrationIssueTitle: 'La reprise doit être vérifiée',
  migrationIssueHint:
    'Vérifiez les justificatifs des lignes source concernées ou de toute la reprise. Après résolution de la cause, poursuivez uniquement par la procédure de migration contrôlée.',
  migrationIssueJob: 'Ensemble de la reprise',
  migrationIssueRow: 'Ligne source {{row}}',
  migrationEntityLabel: 'Reprise de données historiques',

  emptyCells: 'Cellules vides',
  industryCodeHint:
    'Importez les codes de secteur sous forme de texte. Si les valeurs du tableur sont déjà numériques, les zéros initiaux perdus ne peuvent pas être reconstitués de façon fiable. Vérifiez l’aperçu ; aucun zéro n’est ajouté.',
  emptyKeep: 'Cellule vide : conserver la valeur',
  emptyClear: 'Cellule vide : effacer la valeur',
  title: 'Centre d’import',
  subtitle: 'Reprise validée et reprenable des données de base CSV et XLSX.',
  flowSteps: {
    1: 'Téléverser',
    2: 'Détecter le format',
    3: 'Choisir la feuille',
    4: 'Détecter l’en-tête',
    5: 'Choisir la cible',
    6: 'Mapper les colonnes',
    7: 'Transformer',
    8: 'Aperçu',
    9: 'Valider',
    10: 'Résoudre les doublons',
    11: 'Test à blanc',
    12: 'Importer',
    13: 'Rapport',
  },
  uploadTitle: '1–2 · Téléversement et détection',
  uploadHint: 'L’original protégé est archivé dans la GED avec son SHA-256.',
  dropHere: 'Déposer un CSV ou XLSX ici',
  formats: 'CSV/XLSX jusqu’à 50 Mo ; UTF-8, UTF-16 et Windows-1252.',
  chooseFile: 'Choisir un fichier',
  encoding: 'Encodage',
  delimiter: 'Séparateur',
  sourceConfiguration: '3–5 · Source et cible',
  sourceConfigurationHint: 'Vérifiez la feuille, la ligne d’en-tête et l’entité cible.',
  sheet: 'Feuille',
  headerRow: 'Ligne d’en-tête',
  target: 'Entité cible',
  confirmSource: 'Lire la table',
  mappingTitle: '6–10 · Mappage, transformations et doublons',
  mappingHint: '{{rows}} lignes ; {{ignored}} lignes vides ou totales ignorées.',
  requiredOnCreate: 'obligatoire pour les nouvelles fiches',
  productUpdateHint:
    'Choisissez Mettre à jour pour retrouver les articles existants par leur numéro d’article. Seules les valeurs associées changent ; les cellules vides conservent les valeurs existantes par défaut. Les nouveaux articles nécessitent un nom.',
  sourceColumn: 'Colonne source',
  sample: 'Exemples',
  targetField: 'Champ ERP',
  ignore: 'Ignorer',
  transform: 'Transformation',
  none: 'Aucune',
  newCustomField: 'Créer un champ personnalisé',
  localeFormat: 'Format nombres/dates',
  countryMap: 'Pays (source=ISO)',
  unitMap: 'Unités (source=code)',
  taxMap: 'Taxes (source=code)',
  duplicateStrategy: 'Stratégie de doublons',
  runDryRun: 'Lancer le test',
  customProposalTitle: 'Nouveau champ pour « {{column}} »',
  customProposalHint: 'Vérifiez noms, descriptions et catégorie dans chaque langue ERP.',
  technicalKey: 'Clé technique',
  fieldType: 'Type de champ',
  category: 'Catégorie',
  createCategory: 'Créer une catégorie contrôlée',
  categoryKey: 'Clé de catégorie',
  name: 'Nom',
  description: 'Description/aide',
  categoryName: 'Nom de catégorie',
  categoryDescription: 'Description de catégorie',
  validationTitle: '11 · Aperçu et résultat du test',
  validationHint: 'Aucune donnée de production n’a été modifiée.',
  validRows: 'Lignes valides',
  invalidRows: 'Lignes erronées',
  duplicatesFound: 'Doublons',
  willCreate: 'À créer',
  willUpdate: 'À mettre à jour',
  row: 'Ligne',
  field: 'Champ',
  reason: 'Motif',
  manualDecisions: 'Décisions manuelles',
  bulkUpdateUnambiguous: 'Mettre à jour tous les enregistrements associés sans ambiguïté',
  bulkUpdateUnambiguousHint:
    'S’applique à tout l’import, y compris aux lignes hors de cet aperçu. Les décisions individuelles sont conservées. Les correspondances multiples nécessitent une décision. Relancez le test avant de confirmer.',
  dryRunOutdated:
    'Le résultat affiché n’est plus à jour. Vérifiez vos décisions et relancez le test avant de confirmer l’import.',
  manualDecisionProgress:
    '{{shown}} décisions sur {{total}} en attente du dernier test sont affichées. Choisissez les actions pour ces lignes, puis relancez le test pour afficher les décisions restantes.',
  chooseDecision: 'Choisir',
  explicitConfirmation: 'J’ai vérifié l’aperçu et j’autorise explicitement l’import réel.',
  allowPartial: 'Importer les lignes valides malgré {{count}} erreurs.',
  startImport: 'Importer {{count}} lignes',
  importAccepted: 'Import accepté et reprenable.',
  resultTitle: '12–13 · Progression et rapport',
  created: 'créés',
  updated: 'mis à jour',
  skipped: 'ignorés',
  errorReport: 'Télécharger le fichier d’erreurs',
  resume: 'Reprendre',
  newImport: 'Nouvel import',
  sourceFile: 'Fichier source protégé',
  history: 'Historique des imports',
  historyHint: 'Hash, utilisateur, mappage et rapports restent traçables.',
  settingsTitle: 'Conservation et traitement',
  settingsHint: 'Définissez la conservation des sources et le seuil des travaux en file.',
  retentionDays: 'Conservation des sources (jours)',
  largeJobThreshold: 'Mise en file à partir de ce nombre de lignes',
  settingsSaved: 'Paramètres d’import enregistrés.',
  noJobs: 'Aucun import.',
  file: 'Fichier',
  progress: 'Progression',
  status: 'Statut',
  executedAt: 'Terminé',
  rowSummary: '{{success}} réussies · {{failed}} erreurs · {{total}} au total',
  entities: {
    ...entitiesEn,
    CUSTOMER: 'Clients',
    SUPPLIER: 'Fournisseurs',
    PROSPECT: 'Prospects',
    CONTACT: 'Contacts',
    ADDRESS: 'Adresses et adresses de livraison',
    PRODUCT: 'Articles',
    PRODUCT_CATEGORY: 'Catégories de produits',
    PRODUCT_PACKAGING: 'Unités de conditionnement',
    PRODUCT_PRICE: 'Prix et paliers',
    SUPPLIER_PRODUCT: 'Références fournisseur-article',
    SUPPLIER_PRICE: 'Paliers de prix d’achat fournisseur',
    INITIAL_STOCK: 'Stocks initiaux',
    BATCH: 'Lots et dates limite',
    OPEN_ITEM: 'Postes ouverts',
    CUSTOMER_PRICE: 'Prix spécifiques client',
    CUSTOM_FIELD_VALUE: 'Valeurs de champs personnalisés',
  },
  statuses: {
    PENDING: 'En attente',
    MAPPING: 'Mappage',
    VALIDATED: 'Validé',
    QUEUED: 'En file',
    PROCESSING: 'En cours',
    COMPLETED: 'Terminé',
    FAILED: 'Échoué',
    CANCELLED: 'Annulé',
    ROLLED_BACK: 'Annulé par retour',
  },
  fields: {
    ...fieldsEn,
    ...selfImportFieldsFr,
    industryCode: 'Code de secteur',
    externalId: 'ID source externe',
    customerNumber: 'Numéro client/fournisseur',
    companyName: 'Société/nom',
    phone: 'Téléphone',
    street: 'Rue',
    houseNumber: 'Numéro',
    postalCode: 'Code postal',
    city: 'Ville',
    countryCode: 'Pays',
    subdivisionCode: 'Code du département',
    subdivisionName: 'Nom du département',
    region: 'Région',
    legalFormCode: 'Code de forme juridique',
    legalFormName: 'Autre forme juridique',
    legalFormJurisdiction: 'Juridiction',
    responsibilities: 'Responsabilités (tableau JSON)',
    vatId: 'N° TVA',
    partnerExternalId: 'ID partenaire externe',
    firstName: 'Prénom',
    lastName: 'Nom',
    label: 'Libellé d’adresse',
    addressType: 'Type d’adresse',
    isDefaultShipping: 'Adresse de livraison par défaut',
    articleNumber: 'Référence article',
    name: 'Désignation',
    description: 'Description',
    productGroup: 'Groupe de produits',
    baseUnit: 'Unité de base',
    listPrice: 'Prix de vente',
    purchasePrice: 'Prix d’achat',
    taxRateType: 'Taux de TVA',
    minStock: 'Stock minimal',
    slug: 'Clé technique',
    parent: 'Catégorie parente',
    code: 'Code emballage',
    quantity: 'Quantité',
    unit: 'Unité',
    priceGroup: 'Tarif/groupe',
    currency: 'Devise',
    preferredLanguage: 'Langue du document',
    minQuantity: 'Quantité minimale',
    unitPrice: 'Prix unitaire',
    supplierArticleNumber: 'Référence fournisseur',
    leadTimeDays: 'Délai en jours',
    warehouseCode: 'Entrepôt',
    reason: 'Motif',
    batchNumber: 'Numéro de lot',
    expiryDate: 'DLC/DDM',
    productionDate: 'Date de production',
    documentNumber: 'Numéro de document',
    documentDate: 'Date du document',
    dueDate: 'Date d’échéance',
    grossAmount: 'Montant brut',
    paidAmount: 'Montant payé',
    entityType: 'Type d’entité',
    recordKey: 'Clé d’enregistrement',
    fieldKey: 'Clé du champ personnalisé',
    value: 'Valeur',
  },
  fieldTypes: {
    TEXT: 'Texte',
    TEXTAREA: 'Texte long',
    NUMBER: 'Nombre',
    DECIMAL: 'Nombre décimal',
    CHECKBOX: 'Case à cocher',
    MULTI_SELECT: 'Sélection multiple',
    DATE: 'Date',
    BOOLEAN: 'Case à cocher',
    SELECT: 'Liste',
  },
  transformSearch: 'Valeur recherchée',
  transformReplacement: 'Remplacement',
  transformValues: 'Un mappage par ligne : source=cible',
  transformDefault: 'Valeur par défaut si vide',
  transforms: {
    TRIM: 'Supprimer les espaces',
    UPPERCASE: 'Majuscules',
    LOWERCASE: 'Minuscules',
    REPLACE: 'Remplacer le texte',
    VALUE_MAP: 'Mapper les valeurs',
    DEFAULT: 'Définir la valeur par défaut',
  },
  localeFormats: {
    AUTO: 'Détection automatique',
    DE: 'Allemand',
    FR: 'Français',
    INTERNATIONAL: 'International (ISO/US)',
  },
  duplicates: {
    SKIP: 'Ignorer l’existant',
    UPDATE: 'Mettre à jour l’existant',
    CREATE_NEW: 'Créer un nouvel enregistrement',
    MANUAL: 'Décider manuellement',
  },
  profileTitle: 'Profil de migration réutilisable',
  countryMapExample: 'Allemagne=DE',
  unitMapExample: 'Pièce=PCE',
  taxMapExample: '19%=STANDARD',
  profileHint: 'Appliquer un mappage versionné ou enregistrer la configuration vérifiée.',
  profileReviewWarning:
    'Les profils système identifient uniquement la source. Ils ne supposent aucun format tiers ; vérifiez chaque mappage avant le test.',
  profile: 'Profil',
  sourceSystem: 'Système source',
  newProfileName: 'Nom du nouveau profil client',
  saveProfile: 'Enregistrer le mappage',
  updateProfile: 'Enregistrer une nouvelle version',
  archiveProfile: 'Archiver le profil',
  profileSaved: 'Profil d’import enregistré.',
  profileUpdated: 'Nouvelle version du profil enregistrée.',
  profileArchived: 'Profil d’import archivé.',
  profiles: {
    generic: 'CSV/XLSX générique',
    orgamax: 'orgaMAX',
    odoo: 'Odoo',
    logoTiger: 'LOGO/Tiger',
  },
  openHistory: 'Ouvrir l’historique complet',
  historyDetails: 'Détails de l’import',
  historyDetailsHint:
    'Source, paramètres, enregistrements touchés, audit et contrôles d’annulation.',
  mappingVersion: 'Version du mappage',
  dryRunAt: 'Test à blanc',
  auditEntries: 'Entrées d’audit',
  vaultEntries: 'Entrées du coffre GoBD',
  mappingParameters: 'Paramètres exacts de l’import',
  operation: 'Opération',
  affectedRecord: 'Enregistrement touché',
  historyPage: 'Page {{page}} sur {{pages}} · {{total}} lignes',
  rollbackTitle: 'Annulation sécurisée',
  rollbackHint:
    'Seuls les enregistrements inchangés, créés exclusivement par cet import et sans utilisation ultérieure peuvent être supprimés.',
  rollbackPermissionRequired: 'L’autorisation spéciale d’annulation est requise.',
  checkRollback: 'Vérifier l’éligibilité',
  correctionList: 'Télécharger la liste de correction',
  rollbackReason: 'Motif obligatoire',
  rollbackConfirmation:
    'J’ai vérifié le contrôle et confirme explicitement les suppressions indiquées.',
  executeRollback: 'Exécuter l’annulation confirmée',
  rollbackCompleted: 'L’import a été annulé et entièrement audité.',
  rollbackBlocked: 'Les données ont changé ; une liste de correction a été créée.',
  rollbackStatuses: { ELIGIBLE: 'Éligible', BLOCKED: 'Bloqué', COMPLETED: 'Terminé' },
};

export const importCenterEs = {
  ...base,
  referenceMissing: 'Falta la referencia única del registro o de la fuente.',
  migrationIssueUnreadable:
    'La incidencia guardada no se puede leer. Solicite la revisión de la prueba original antes de continuar.',
  migrationIssueTitle: 'La importación requiere revisión',
  migrationIssueHint:
    'Revise las pruebas de las filas de origen afectadas o de toda la importación. Tras resolver la causa, continúe únicamente mediante el proceso de migración controlado.',
  migrationIssueJob: 'Toda la importación',
  migrationIssueRow: 'Fila de origen {{row}}',
  migrationEntityLabel: 'Importación de datos históricos',

  emptyCells: 'Celdas vacías',
  industryCodeHint:
    'Importe los códigos de sector como texto. Si los valores de la hoja ya son numéricos, los ceros iniciales perdidos no pueden reconstruirse de forma fiable. Revise la vista previa; no se añaden ceros.',
  emptyKeep: 'Celda vacía: conservar el valor',
  emptyClear: 'Celda vacía: borrar el valor',
  title: 'Centro de importación',
  subtitle: 'Transferencia validada y reanudable de datos maestros CSV y XLSX.',
  flowSteps: {
    1: 'Subir archivo',
    2: 'Detectar formato',
    3: 'Elegir hoja',
    4: 'Detectar cabecera',
    5: 'Elegir destino',
    6: 'Asignar columnas',
    7: 'Transformar',
    8: 'Vista previa',
    9: 'Validar',
    10: 'Resolver duplicados',
    11: 'Prueba',
    12: 'Importar',
    13: 'Informe',
  },
  uploadTitle: '1–2 · Carga y detección',
  uploadHint: 'El original protegido se archiva en el DMS con SHA-256.',
  dropHere: 'Suelta aquí CSV o XLSX',
  formats: 'CSV/XLSX hasta 50 MB; UTF-8, UTF-16 y Windows-1252.',
  chooseFile: 'Elegir archivo',
  encoding: 'Codificación',
  delimiter: 'Separador',
  sourceConfiguration: '3–5 · Origen y destino',
  sourceConfigurationHint: 'Revisa hoja, fila de cabecera y entidad destino.',
  sheet: 'Hoja',
  headerRow: 'Fila de cabecera',
  target: 'Entidad destino',
  confirmSource: 'Leer tabla',
  mappingTitle: '6–10 · Asignación, transformación y duplicados',
  mappingHint: '{{rows}} filas; {{ignored}} filas vacías o totales ignoradas.',
  requiredOnCreate: 'obligatorio para registros nuevos',
  productUpdateHint:
    'Elija Actualizar para identificar artículos existentes por su número de artículo. Solo cambian los valores asignados; las celdas vacías conservan los valores existentes por defecto. Los artículos nuevos requieren un nombre.',
  sourceColumn: 'Columna origen',
  sample: 'Ejemplos',
  targetField: 'Campo ERP',
  ignore: 'Ignorar',
  transform: 'Transformación',
  none: 'Ninguna',
  newCustomField: 'Crear campo adicional',
  localeFormat: 'Formato numérico/fecha',
  countryMap: 'Países (origen=ISO)',
  unitMap: 'Unidades (origen=código)',
  taxMap: 'Impuestos (origen=código)',
  duplicateStrategy: 'Estrategia de duplicados',
  runDryRun: 'Ejecutar prueba',
  customProposalTitle: 'Nuevo campo para «{{column}}»',
  customProposalHint: 'Revisa nombres, descripciones y categoría en cada idioma ERP.',
  technicalKey: 'Clave técnica',
  fieldType: 'Tipo de campo',
  category: 'Categoría',
  createCategory: 'Crear categoría controlada',
  categoryKey: 'Clave de categoría',
  name: 'Nombre',
  description: 'Descripción/ayuda',
  categoryName: 'Nombre de categoría',
  categoryDescription: 'Descripción de categoría',
  validationTitle: '11 · Vista previa y prueba',
  validationHint: 'No se han modificado datos productivos.',
  validRows: 'Filas válidas',
  invalidRows: 'Filas con errores',
  duplicatesFound: 'Duplicados',
  willCreate: 'Se crearán',
  willUpdate: 'Se actualizarán',
  row: 'Fila',
  field: 'Campo',
  reason: 'Motivo',
  manualDecisions: 'Decisiones manuales',
  bulkUpdateUnambiguous: 'Actualizar todos los registros con coincidencia inequívoca',
  bulkUpdateUnambiguousHint:
    'Se aplica a toda la importación, incluidas las filas fuera de esta vista previa. Se conservan las decisiones individuales. Las coincidencias múltiples siguen requiriendo una decisión. Repita la prueba antes de confirmar.',
  dryRunOutdated:
    'El resultado mostrado ya no está actualizado. Revise sus decisiones y ejecute otra prueba antes de confirmar la importación.',
  manualDecisionProgress:
    'Se muestran {{shown}} de {{total}} decisiones pendientes de la última prueba. Elija las acciones para estas filas y repita la prueba para ver las decisiones restantes.',
  chooseDecision: 'Elegir',
  explicitConfirmation:
    'He revisado la vista previa y autorizo expresamente la importación productiva.',
  allowPartial: 'Importar filas válidas pese a {{count}} errores.',
  startImport: 'Importar {{count}} filas',
  importAccepted: 'Importación aceptada y reanudable.',
  resultTitle: '12–13 · Progreso e informe',
  created: 'creados',
  updated: 'actualizados',
  skipped: 'omitidos',
  errorReport: 'Descargar archivo de errores',
  resume: 'Reanudar',
  newImport: 'Nueva importación',
  sourceFile: 'Archivo origen protegido',
  history: 'Historial de importación',
  historyHint: 'Hash, usuario, asignación e informes son trazables.',
  settingsTitle: 'Conservación y procesamiento',
  settingsHint: 'Configure la conservación de fuentes y el umbral para trabajos en cola.',
  retentionDays: 'Conservación de archivos fuente (días)',
  largeJobThreshold: 'Poner en cola desde este número de filas',
  settingsSaved: 'Configuración de importación guardada.',
  noJobs: 'Sin importaciones.',
  file: 'Archivo',
  progress: 'Progreso',
  status: 'Estado',
  executedAt: 'Finalizado',
  rowSummary: '{{success}} correctas · {{failed}} errores · {{total}} total',
  entities: {
    ...entitiesEn,
    CUSTOMER: 'Clientes',
    SUPPLIER: 'Proveedores',
    PROSPECT: 'Prospectos',
    CONTACT: 'Contactos',
    ADDRESS: 'Direcciones y entregas',
    PRODUCT: 'Artículos',
    PRODUCT_CATEGORY: 'Categorías de producto',
    PRODUCT_PACKAGING: 'Unidades de embalaje',
    PRODUCT_PRICE: 'Precios y tramos',
    SUPPLIER_PRODUCT: 'Relaciones proveedor-artículo',
    SUPPLIER_PRICE: 'Escalas de precios de compra por proveedor',
    INITIAL_STOCK: 'Existencias iniciales',
    BATCH: 'Lotes y caducidad',
    OPEN_ITEM: 'Partidas abiertas',
    CUSTOMER_PRICE: 'Precios específicos de cliente',
    CUSTOM_FIELD_VALUE: 'Valores de campos adicionales',
  },
  statuses: {
    PENDING: 'Pendiente',
    MAPPING: 'Asignación',
    VALIDATED: 'Validado',
    QUEUED: 'En cola',
    PROCESSING: 'En proceso',
    COMPLETED: 'Completado',
    FAILED: 'Fallido',
    CANCELLED: 'Cancelado',
    ROLLED_BACK: 'Revertido',
  },
  fields: {
    ...fieldsEn,
    ...selfImportFieldsEs,
    industryCode: 'Código de sector',
    externalId: 'ID de origen externo',
    customerNumber: 'Número cliente/proveedor',
    companyName: 'Empresa/nombre',
    phone: 'Teléfono',
    street: 'Calle',
    houseNumber: 'Número',
    postalCode: 'Código postal',
    city: 'Ciudad',
    countryCode: 'País',
    subdivisionCode: 'Código del departamento',
    subdivisionName: 'Nombre del departamento',
    region: 'Región',
    legalFormCode: 'Código de forma jurídica',
    legalFormName: 'Otra forma jurídica',
    legalFormJurisdiction: 'Jurisdicción',
    responsibilities: 'Responsabilidades (array JSON)',
    vatId: 'NIF-IVA',
    firstName: 'Nombre',
    lastName: 'Apellidos',
    label: 'Etiqueta de dirección',
    addressType: 'Tipo de dirección',
    articleNumber: 'Número de artículo',
    name: 'Nombre',
    description: 'Descripción',
    productGroup: 'Grupo de productos',
    baseUnit: 'Unidad base',
    listPrice: 'Precio de venta',
    purchasePrice: 'Precio de compra',
    taxRateType: 'Tipo impositivo',
    minStock: 'Stock mínimo',
    parent: 'Categoría superior',
    quantity: 'Cantidad',
    unit: 'Unidad',
    currency: 'Moneda',
    preferredLanguage: 'Idioma del documento',
    minQuantity: 'Cantidad mínima',
    unitPrice: 'Precio unitario',
    warehouseCode: 'Almacén',
    reason: 'Motivo',
    batchNumber: 'Número de lote',
    expiryDate: 'Caducidad',
    productionDate: 'Fecha de producción',
    documentNumber: 'Número de documento',
    documentDate: 'Fecha de documento',
    dueDate: 'Vencimiento',
    grossAmount: 'Importe bruto',
    paidAmount: 'Importe pagado',
    entityType: 'Tipo de entidad',
    recordKey: 'Clave de registro',
    fieldKey: 'Clave de campo adicional',
    value: 'Valor',
  },
  fieldTypes: {
    TEXT: 'Texto',
    TEXTAREA: 'Texto largo',
    NUMBER: 'Número',
    DECIMAL: 'Número decimal',
    CHECKBOX: 'Casilla de verificación',
    MULTI_SELECT: 'Selección múltiple',
    DATE: 'Fecha',
    BOOLEAN: 'Casilla',
    SELECT: 'Selección',
  },
  transformSearch: 'Valor buscado',
  transformReplacement: 'Sustitución',
  transformValues: 'Una asignación por línea: origen=destino',
  transformDefault: 'Valor predeterminado si está vacío',
  transforms: {
    TRIM: 'Quitar espacios',
    UPPERCASE: 'Mayúsculas',
    LOWERCASE: 'Minúsculas',
    REPLACE: 'Sustituir texto',
    VALUE_MAP: 'Asignar valores',
    DEFAULT: 'Definir valor predeterminado',
  },
  localeFormats: {
    AUTO: 'Detectar automáticamente',
    DE: 'Alemán',
    FR: 'Francés',
    INTERNATIONAL: 'Internacional (ISO/US)',
  },
  duplicates: {
    SKIP: 'Omitir existente',
    UPDATE: 'Actualizar existente',
    CREATE_NEW: 'Crear nuevo registro',
    MANUAL: 'Decidir manualmente',
  },
  profileTitle: 'Perfil de migración reutilizable',
  countryMapExample: 'Alemania=DE',
  unitMapExample: 'Pieza=PCE',
  taxMapExample: '19%=STANDARD',
  profileHint: 'Aplicar una asignación versionada o guardar la configuración revisada.',
  profileReviewWarning:
    'Los perfiles del sistema solo identifican el origen. No presuponen formatos externos; revise cada asignación antes de la prueba.',
  profile: 'Perfil',
  sourceSystem: 'Sistema de origen',
  newProfileName: 'Nombre del nuevo perfil de cliente',
  saveProfile: 'Guardar asignación como perfil',
  updateProfile: 'Guardar nueva versión',
  archiveProfile: 'Archivar perfil',
  profileSaved: 'Perfil de importación guardado.',
  profileUpdated: 'Se guardó una nueva versión del perfil.',
  profileArchived: 'Perfil de importación archivado.',
  profiles: {
    generic: 'CSV/XLSX genérico',
    orgamax: 'orgaMAX',
    odoo: 'Odoo',
    logoTiger: 'LOGO/Tiger',
  },
  openHistory: 'Abrir historial completo',
  historyDetails: 'Detalles de la importación',
  historyDetailsHint:
    'Origen, parámetros, registros afectados, auditoría y controles de reversión.',
  mappingVersion: 'Versión de asignación',
  dryRunAt: 'Prueba',
  auditEntries: 'Entradas de auditoría',
  vaultEntries: 'Entradas del registro GoBD',
  mappingParameters: 'Parámetros exactos de importación',
  operation: 'Operación',
  affectedRecord: 'Registro afectado',
  historyPage: 'Página {{page}} de {{pages}} · {{total}} filas',
  rollbackTitle: 'Reversión segura',
  rollbackHint:
    'Solo se eliminan registros sin cambios, creados exclusivamente por esta importación y sin uso posterior.',
  rollbackPermissionRequired: 'Se requiere el permiso especial de reversión.',
  checkRollback: 'Comprobar reversión',
  correctionList: 'Descargar lista de corrección',
  rollbackReason: 'Motivo obligatorio',
  rollbackConfirmation:
    'He revisado la comprobación y confirmo expresamente las eliminaciones indicadas.',
  executeRollback: 'Ejecutar reversión confirmada',
  rollbackCompleted: 'La importación se revirtió con auditoría completa.',
  rollbackBlocked: 'Los datos cambiaron; se generó una lista de corrección.',
  rollbackStatuses: { ELIGIBLE: 'Permitido', BLOCKED: 'Bloqueado', COMPLETED: 'Completado' },
};

export const importCenterTr = {
  ...base,
  referenceMissing: 'Benzersiz ana veri veya kaynak bağlantısı eksik.',
  migrationIssueUnreadable:
    'Kaydedilen bildirim okunamıyor. Devam etmeden önce özgün kanıtı kontrol ettirin.',
  migrationIssueTitle: 'Aktarımın incelenmesi gerekiyor',
  migrationIssueHint:
    'Etkilenen kaynak satırlarının veya tüm aktarımın kanıtlarını inceleyin. Nedeni giderdikten sonra yalnızca kontrollü aktarım süreci üzerinden devam edin.',
  migrationIssueJob: 'Tüm aktarım',
  migrationIssueRow: 'Kaynak satırı {{row}}',
  migrationEntityLabel: 'Geçmiş veri aktarımı',

  emptyCells: 'Boş hücreler',
  industryCodeHint:
    'Sektör kodlarını metin olarak içe aktarın. Elektronik tablo değerleri zaten sayısalsa kaybolan baştaki sıfırlar güvenilir biçimde geri getirilemez. Önizlemeyi kontrol edin; sıfır eklenmez.',
  emptyKeep: 'Boş hücre: mevcut değeri koru',
  emptyClear: 'Boş hücre: değeri sil',
  title: 'İçe aktarma merkezi',
  subtitle: 'CSV ve XLSX ana verilerinin doğrulanmış, sürdürülebilir aktarımı.',
  flowSteps: {
    1: 'Dosya yükle',
    2: 'Biçimi algıla',
    3: 'Sayfa seç',
    4: 'Başlığı algıla',
    5: 'Hedef seç',
    6: 'Sütunları eşle',
    7: 'Dönüştür',
    8: 'Önizleme',
    9: 'Doğrula',
    10: 'Mükerrerleri çöz',
    11: 'Deneme',
    12: 'İçe aktar',
    13: 'Rapor',
  },
  uploadTitle: '1–2 · Yükleme ve algılama',
  uploadHint: 'Korunan özgün dosya SHA-256 ile DMS’ye kaydedilir.',
  dropHere: 'CSV veya XLSX dosyasını buraya bırakın',
  formats: '50 MB’a kadar CSV/XLSX; UTF-8, UTF-16 ve Windows-1252.',
  chooseFile: 'Dosya seç',
  encoding: 'Kodlama',
  delimiter: 'Ayırıcı',
  sourceConfiguration: '3–5 · Kaynak ve hedef',
  sourceConfigurationHint: 'Sayfayı, başlık satırını ve hedef varlığı kontrol edin.',
  sheet: 'Çalışma sayfası',
  headerRow: 'Başlık satırı',
  target: 'Hedef varlık',
  confirmSource: 'Tabloyu oku',
  mappingTitle: '6–10 · Eşleme, dönüşüm ve mükerrerler',
  mappingHint: '{{rows}} veri satırı; {{ignored}} boş/toplam satırı yok sayıldı.',
  requiredOnCreate: 'yalnızca yeni kayıtlar için zorunlu',
  productUpdateHint:
    'Mevcut ürünleri ürün numarası ile bulmak için Güncelle seçeneğini kullanın. Yalnızca eşlenen değerler değişir; boş hücreler varsayılan olarak mevcut değerleri korur. Yeni ürünler için ad zorunludur.',
  sourceColumn: 'Kaynak sütun',
  sample: 'Örnekler',
  targetField: 'ERP alanı',
  ignore: 'Yok say',
  transform: 'Dönüşüm',
  none: 'Yok',
  newCustomField: 'Ek alan oluştur',
  localeFormat: 'Sayı/tarih biçimi',
  countryMap: 'Ülke eşlemesi (kaynak=ISO)',
  unitMap: 'Birim eşlemesi (kaynak=kod)',
  taxMap: 'Vergi eşlemesi (kaynak=kod)',
  duplicateStrategy: 'Mükerrer stratejisi',
  runDryRun: 'Denemeyi çalıştır',
  customProposalTitle: '“{{column}}” için yeni ek alan',
  customProposalHint: 'Ad, açıklama ve kategoriyi tüm ERP dillerinde denetleyin.',
  technicalKey: 'Teknik anahtar',
  fieldType: 'Alan türü',
  category: 'Kategori',
  createCategory: 'Kontrollü kategori oluştur',
  categoryKey: 'Kategori anahtarı',
  name: 'Ad',
  description: 'Açıklama/yardım',
  categoryName: 'Kategori adı',
  categoryDescription: 'Kategori açıklaması',
  validationTitle: '11 · Önizleme ve deneme sonucu',
  validationHint: 'Canlı veriler henüz değiştirilmedi.',
  validRows: 'Geçerli satırlar',
  invalidRows: 'Hatalı satırlar',
  duplicatesFound: 'Mükerrerler',
  willCreate: 'Oluşturulacak',
  willUpdate: 'Güncellenecek',
  row: 'Satır',
  field: 'Alan',
  reason: 'Neden',
  manualDecisions: 'Elle mükerrer kararları',
  bulkUpdateUnambiguous: 'Kesin olarak eşleşen tüm kayıtları güncelle',
  bulkUpdateUnambiguousHint:
    'Önizlemede görünmeyen satırlar dahil tüm içe aktarıma uygulanır. Tek tek verilen kararlar korunur. Birden fazla eşleşme için karar vermek gerekir. Onaylamadan önce testi yeniden çalıştırın.',
  dryRunOutdated:
    'Gösterilen sonuç artık güncel değil. Kararlarınızı gözden geçirin ve içe aktarmayı onaylamadan önce testi yeniden çalıştırın.',
  manualDecisionProgress:
    'Son testte bekleyen {{total}} karardan {{shown}} tanesi gösteriliyor. Bu satırlar için işlemleri seçin, ardından kalan kararları görmek için testi yeniden çalıştırın.',
  chooseDecision: 'Karar seç',
  explicitConfirmation: 'Önizlemeyi denetledim ve canlı içe aktarmayı açıkça onaylıyorum.',
  allowPartial: '{{count}} hatalı satıra rağmen geçerli satırları aktar.',
  startImport: '{{count}} satırı içe aktar',
  importAccepted: 'İçe aktarma kabul edildi ve güvenle sürdürülebilir.',
  resultTitle: '12–13 · İlerleme ve sonuç',
  created: 'oluşturuldu',
  updated: 'güncellendi',
  skipped: 'atlandı',
  errorReport: 'Düzeltilebilir hata dosyasını indir',
  resume: 'Sürdür',
  newImport: 'Yeni içe aktarma',
  sourceFile: 'Korunan kaynak dosya',
  history: 'İçe aktarma geçmişi',
  historyHint: 'Hash, kullanıcı, eşleme ve raporlar izlenebilir kalır.',
  settingsTitle: 'Saklama ve işleme',
  settingsHint: 'Kaynak dosya saklama süresini ve kuyruk satır eşiğini ayarlayın.',
  retentionDays: 'Kaynak dosya saklama süresi (gün)',
  largeJobThreshold: 'Bu satır sayısından itibaren kuyruğa al',
  settingsSaved: 'İçe aktarma ayarları kaydedildi.',
  noJobs: 'Henüz içe aktarma yok.',
  file: 'Dosya',
  progress: 'İlerleme',
  status: 'Durum',
  executedAt: 'Tamamlandı',
  rowSummary: '{{success}} başarılı · {{failed}} hata · toplam {{total}}',
  entities: {
    ...entitiesEn,
    CUSTOMER: 'Müşteriler',
    SUPPLIER: 'Tedarikçiler',
    PROSPECT: 'Potansiyel müşteriler',
    CONTACT: 'İlgili kişiler',
    ADDRESS: 'Adresler ve teslimat adresleri',
    PRODUCT: 'Ürünler',
    PRODUCT_CATEGORY: 'Ürün kategorileri',
    PRODUCT_PACKAGING: 'Ambalaj birimleri',
    PRODUCT_PRICE: 'Fiyatlar ve kademeler',
    SUPPLIER_PRODUCT: 'Tedarikçi-ürün eşlemeleri',
    SUPPLIER_PRICE: 'Tedarikçi alış fiyatı kademeleri',
    INITIAL_STOCK: 'Açılış stokları',
    BATCH: 'Partiler ve SKT',
    OPEN_ITEM: 'Açık kalemler',
    CUSTOMER_PRICE: 'Müşteriye özel fiyatlar',
    CUSTOM_FIELD_VALUE: 'Ek alan değerleri',
  },
  statuses: {
    PENDING: 'Bekliyor',
    MAPPING: 'Eşleme',
    VALIDATED: 'Doğrulandı',
    QUEUED: 'Kuyrukta',
    PROCESSING: 'İşleniyor',
    COMPLETED: 'Tamamlandı',
    FAILED: 'Başarısız',
    CANCELLED: 'İptal',
    ROLLED_BACK: 'Geri alındı',
  },
  fields: {
    ...fieldsEn,
    ...selfImportFieldsTr,
    industryCode: 'Sektör kodu',
    externalId: 'Harici kaynak kimliği',
    customerNumber: 'Müşteri/tedarikçi numarası',
    companyName: 'Şirket/ad',
    email: 'E-posta',
    phone: 'Telefon',
    street: 'Sokak',
    houseNumber: 'Kapı numarası',
    postalCode: 'Posta kodu',
    city: 'Şehir',
    countryCode: 'Ülke',
    subdivisionCode: 'Departman kodu',
    subdivisionName: 'Departman adı',
    region: 'Bölge',
    legalFormCode: 'Hukuki biçim kodu',
    legalFormName: 'Diğer hukuki biçim adı',
    legalFormJurisdiction: 'Yargı alanı',
    responsibilities: 'Sorumluluklar (JSON dizisi)',
    vatId: 'KDV kimliği',
    firstName: 'Ad',
    lastName: 'Soyad',
    position: 'Görev',
    label: 'Adres etiketi',
    addressType: 'Adres türü',
    articleNumber: 'Ürün numarası',
    name: 'Ad',
    description: 'Açıklama',
    productGroup: 'Ürün grubu',
    baseUnit: 'Temel birim',
    listPrice: 'Satış fiyatı',
    purchasePrice: 'Alış fiyatı',
    taxRateType: 'Vergi oranı',
    minStock: 'Asgari stok',
    parent: 'Üst kategori',
    quantity: 'Miktar',
    unit: 'Birim',
    currency: 'Para birimi',
    preferredLanguage: 'Belge dili',
    minQuantity: 'Asgari miktar',
    unitPrice: 'Birim fiyat',
    warehouseCode: 'Depo',
    reason: 'Neden',
    batchNumber: 'Parti numarası',
    expiryDate: 'Son kullanma tarihi',
    productionDate: 'Üretim tarihi',
    documentNumber: 'Belge numarası',
    documentDate: 'Belge tarihi',
    dueDate: 'Vade tarihi',
    grossAmount: 'Brüt tutar',
    paidAmount: 'Ödenen tutar',
    entityType: 'Varlık türü',
    recordKey: 'Kayıt anahtarı',
    fieldKey: 'Ek alan anahtarı',
    value: 'Değer',
  },
  fieldTypes: {
    TEXT: 'Metin',
    TEXTAREA: 'Uzun metin',
    NUMBER: 'Sayı',
    DECIMAL: 'Ondalık sayı',
    CHECKBOX: 'Onay kutusu',
    MULTI_SELECT: 'Çoklu seçim',
    DATE: 'Tarih',
    BOOLEAN: 'Onay kutusu',
    SELECT: 'Seçim',
  },
  transformSearch: 'Aranacak değer',
  transformReplacement: 'Yeni değer',
  transformValues: 'Her satıra bir eşleme: kaynak=hedef',
  transformDefault: 'Boş değerler için varsayılan',
  transforms: {
    TRIM: 'Boşlukları kaldır',
    UPPERCASE: 'Büyük harf',
    LOWERCASE: 'Küçük harf',
    REPLACE: 'Metni değiştir',
    VALUE_MAP: 'Değerleri eşle',
    DEFAULT: 'Varsayılanı ayarla',
  },
  localeFormats: {
    AUTO: 'Otomatik algıla',
    DE: 'Almanca',
    FR: 'Fransızca',
    INTERNATIONAL: 'Uluslararası (ISO/US)',
  },
  duplicates: {
    SKIP: 'Mevcut kaydı atla',
    UPDATE: 'Mevcut kaydı güncelle',
    CREATE_NEW: 'Yeni kayıt oluştur',
    MANUAL: 'Elle karar ver',
  },
  profileTitle: 'Yeniden kullanılabilir geçiş profili',
  countryMapExample: 'Almanya=DE',
  unitMapExample: 'Adet=PCE',
  taxMapExample: '19%=STANDARD',
  profileHint: 'Sürümlü eşlemeyi uygulayın veya incelenen yapılandırmayı kaydedin.',
  profileReviewWarning:
    'Sistem profilleri yalnızca kaynağı tanımlar. Harici biçim varsaymaz; testten önce her eşlemeyi inceleyin.',
  profile: 'Profil',
  sourceSystem: 'Kaynak sistem',
  newProfileName: 'Yeni müşteri profili adı',
  saveProfile: 'Eşlemeyi profil olarak kaydet',
  updateProfile: 'Yeni profil sürümünü kaydet',
  archiveProfile: 'Profili arşivle',
  profileSaved: 'İçe aktarma profili kaydedildi.',
  profileUpdated: 'Yeni profil sürümü kaydedildi.',
  profileArchived: 'İçe aktarma profili arşivlendi.',
  profiles: {
    generic: 'Genel CSV/XLSX',
    orgamax: 'orgaMAX',
    odoo: 'Odoo',
    logoTiger: 'LOGO/Tiger',
  },
  openHistory: 'Tam geçmişi aç',
  historyDetails: 'İçe aktarma ayrıntıları',
  historyDetailsHint: 'Kaynak, parametreler, etkilenen kayıtlar, denetim ve geri alma kontrolleri.',
  mappingVersion: 'Eşleme sürümü',
  dryRunAt: 'Test çalıştırması',
  auditEntries: 'Denetim kayıtları',
  vaultEntries: 'GoBD kasa kayıtları',
  mappingParameters: 'Kesin içe aktarma parametreleri',
  operation: 'İşlem',
  affectedRecord: 'Etkilenen kayıt',
  historyPage: 'Sayfa {{page}} / {{pages}} · {{total}} satır',
  rollbackTitle: 'Güvenli geri alma',
  rollbackHint:
    'Yalnızca bu içe aktarmanın oluşturduğu, değişmemiş ve sonraki kullanım bulunmayan kayıtlar silinebilir.',
  rollbackPermissionRequired: 'Özel geri alma izni gereklidir.',
  checkRollback: 'Geri alma uygunluğunu denetle',
  correctionList: 'Düzeltme listesini indir',
  rollbackReason: 'Zorunlu gerekçe',
  rollbackConfirmation: 'Denetimi inceledim ve belirtilen silmeleri açıkça onaylıyorum.',
  executeRollback: 'Onaylanan geri almayı çalıştır',
  rollbackCompleted: 'İçe aktarma eksiksiz denetimle geri alındı.',
  rollbackBlocked: 'Veriler değişti; bunun yerine düzeltme listesi oluşturuldu.',
  rollbackStatuses: { ELIGIBLE: 'Uygun', BLOCKED: 'Engellendi', COMPLETED: 'Tamamlandı' },
};

export const importCenterRu = {
  ...base,
  referenceMissing: 'Отсутствует однозначная ссылка на запись или источник.',
  title: 'Центр импорта',
  subtitle: 'Проверяемый и возобновляемый перенос справочников CSV/XLSX.',
  flowSteps: {
    1: 'Загрузить файл',
    2: 'Определить формат',
    3: 'Выбрать лист',
    4: 'Найти заголовок',
    5: 'Выбрать цель',
    6: 'Сопоставить колонки',
    7: 'Преобразовать',
    8: 'Предпросмотр',
    9: 'Проверить',
    10: 'Решить дубликаты',
    11: 'Пробный запуск',
    12: 'Импорт',
    13: 'Отчёт',
  },
  uploadTitle: '1–2 · Загрузка и определение',
  uploadHint: 'Защищённый оригинал хранится в DMS с SHA-256.',
  dropHere: 'Перетащите CSV или XLSX сюда',
  formats: 'CSV/XLSX до 50 МБ; UTF-8, UTF-16 и Windows-1252.',
  chooseFile: 'Выбрать файл',
  encoding: 'Кодировка',
  delimiter: 'Разделитель',
  sourceConfiguration: '3–5 · Источник и цель',
  sourceConfigurationHint: 'Проверьте лист, строку заголовка и целевую сущность.',
  sheet: 'Лист',
  headerRow: 'Строка заголовка',
  target: 'Целевая сущность',
  confirmSource: 'Прочитать таблицу',
  mappingTitle: '6–10 · Сопоставление, преобразование и дубликаты',
  mappingHint: '{{rows}} строк; пропущено пустых/итоговых: {{ignored}}.',
  sourceColumn: 'Исходная колонка',
  sample: 'Примеры',
  targetField: 'Поле ERP',
  ignore: 'Пропустить',
  transform: 'Преобразование',
  none: 'Нет',
  newCustomField: 'Создать доп. поле',
  localeFormat: 'Формат чисел/дат',
  countryMap: 'Страны (источник=ISO)',
  unitMap: 'Единицы (источник=код)',
  taxMap: 'Налоги (источник=код)',
  duplicateStrategy: 'Стратегия дубликатов',
  runDryRun: 'Запустить пробу',
  customProposalTitle: 'Новое поле для «{{column}}»',
  customProposalHint: 'Проверьте названия, описания и категорию на всех языках ERP.',
  technicalKey: 'Технический ключ',
  fieldType: 'Тип поля',
  category: 'Категория',
  createCategory: 'Создать контролируемую категорию',
  categoryKey: 'Ключ категории',
  name: 'Название',
  description: 'Описание/справка',
  categoryName: 'Название категории',
  categoryDescription: 'Описание категории',
  validationTitle: '11 · Предпросмотр и пробный результат',
  validationHint: 'Рабочие данные не изменены.',
  validRows: 'Корректные строки',
  invalidRows: 'Ошибочные строки',
  duplicatesFound: 'Дубликаты',
  willCreate: 'Будут созданы',
  willUpdate: 'Будут обновлены',
  row: 'Строка',
  field: 'Поле',
  reason: 'Причина',
  manualDecisions: 'Ручные решения по дубликатам',
  bulkUpdateUnambiguous: 'Обновить все однозначно сопоставленные записи',
  bulkUpdateUnambiguousHint:
    'Применяется ко всему импорту, включая строки вне предпросмотра. Индивидуальные решения сохраняются. При нескольких совпадениях по-прежнему нужно выбрать действие. Перед подтверждением повторите пробный запуск.',
  dryRunOutdated:
    'Показанный результат устарел. Проверьте решения и повторите тест перед подтверждением импорта.',
  manualDecisionProgress:
    'Показано {{shown}} из {{total}} ожидающих решений последнего теста. Выберите действия для этих строк, затем повторите тест, чтобы увидеть оставшиеся решения.',
  chooseDecision: 'Выбрать решение',
  explicitConfirmation: 'Я проверил предпросмотр и явно разрешаю рабочий импорт.',
  allowPartial: 'Импортировать корректные строки несмотря на {{count}} ошибок.',
  startImport: 'Импортировать {{count}} строк',
  importAccepted: 'Импорт принят и может быть безопасно возобновлён.',
  resultTitle: '12–13 · Ход и отчёт',
  created: 'создано',
  updated: 'обновлено',
  skipped: 'пропущено',
  errorReport: 'Скачать файл ошибок',
  resume: 'Возобновить',
  newImport: 'Новый импорт',
  sourceFile: 'Защищённый исходный файл',
  history: 'История импорта',
  historyHint: 'Хеш, пользователь, сопоставление и отчёты остаются прослеживаемыми.',
  settingsTitle: 'Хранение и обработка',
  settingsHint: 'Настройте срок хранения источников и порог постановки задания в очередь.',
  retentionDays: 'Хранение исходных файлов (дни)',
  largeJobThreshold: 'Ставить в очередь с этого числа строк',
  settingsSaved: 'Настройки импорта сохранены.',
  noJobs: 'Импортов пока нет.',
  file: 'Файл',
  progress: 'Прогресс',
  status: 'Статус',
  executedAt: 'Завершено',
  rowSummary: '{{success}} успешно · {{failed}} ошибок · всего {{total}}',
  entities: {
    ...entitiesEn,
    CUSTOMER: 'Клиенты',
    SUPPLIER: 'Поставщики',
    PROSPECT: 'Потенциальные клиенты',
    CONTACT: 'Контактные лица',
    ADDRESS: 'Адреса и адреса доставки',
    PRODUCT: 'Товары',
    PRODUCT_CATEGORY: 'Категории товаров',
    PRODUCT_PACKAGING: 'Единицы упаковки',
    PRODUCT_PRICE: 'Цены и ступени',
    SUPPLIER_PRODUCT: 'Связи поставщик-товар',
    SUPPLIER_PRICE: 'Ступени закупочных цен поставщика',
    INITIAL_STOCK: 'Начальные остатки',
    BATCH: 'Партии и сроки годности',
    OPEN_ITEM: 'Открытые позиции',
    CUSTOMER_PRICE: 'Индивидуальные цены',
    CUSTOM_FIELD_VALUE: 'Значения доп. полей',
  },
  statuses: {
    PENDING: 'Ожидает',
    MAPPING: 'Сопоставление',
    VALIDATED: 'Проверен',
    QUEUED: 'В очереди',
    PROCESSING: 'Выполняется',
    COMPLETED: 'Завершён',
    FAILED: 'Ошибка',
    CANCELLED: 'Отменён',
    ROLLED_BACK: 'Откачен',
  },
  fields: {
    ...fieldsEn,
    ...selfImportFieldsRu,
    externalId: 'Внешний ID источника',
    customerNumber: 'Номер клиента/поставщика',
    companyName: 'Компания/имя',
    email: 'Эл. почта',
    phone: 'Телефон',
    street: 'Улица',
    houseNumber: 'Номер дома',
    postalCode: 'Индекс',
    city: 'Город',
    countryCode: 'Страна',
    vatId: 'Номер НДС',
    firstName: 'Имя',
    lastName: 'Фамилия',
    position: 'Должность',
    label: 'Название адреса',
    addressType: 'Тип адреса',
    articleNumber: 'Артикул',
    name: 'Название',
    description: 'Описание',
    productGroup: 'Группа товаров',
    baseUnit: 'Базовая единица',
    listPrice: 'Цена продажи',
    purchasePrice: 'Цена закупки',
    taxRateType: 'Ставка налога',
    minStock: 'Минимальный запас',
    parent: 'Родительская категория',
    quantity: 'Количество',
    unit: 'Единица',
    currency: 'Валюта',
    minQuantity: 'Минимальное количество',
    unitPrice: 'Цена за единицу',
    warehouseCode: 'Склад',
    reason: 'Причина',
    batchNumber: 'Номер партии',
    expiryDate: 'Срок годности',
    productionDate: 'Дата производства',
    documentNumber: 'Номер документа',
    documentDate: 'Дата документа',
    dueDate: 'Срок оплаты',
    grossAmount: 'Сумма брутто',
    paidAmount: 'Оплачено',
    entityType: 'Тип сущности',
    recordKey: 'Ключ записи',
    fieldKey: 'Ключ доп. поля',
    value: 'Значение',
  },
  fieldTypes: {
    TEXT: 'Текст',
    TEXTAREA: 'Длинный текст',
    NUMBER: 'Число',
    DECIMAL: 'Десятичное число',
    CHECKBOX: 'Флажок',
    MULTI_SELECT: 'Множественный выбор',
    DATE: 'Дата',
    BOOLEAN: 'Флажок',
    SELECT: 'Выбор',
  },
  transformSearch: 'Искомое значение',
  transformReplacement: 'Замена',
  transformValues: 'Одно сопоставление на строку: источник=цель',
  transformDefault: 'Значение по умолчанию для пустых',
  transforms: {
    TRIM: 'Убрать пробелы',
    UPPERCASE: 'Верхний регистр',
    LOWERCASE: 'Нижний регистр',
    REPLACE: 'Заменить текст',
    VALUE_MAP: 'Сопоставить значения',
    DEFAULT: 'Задать значение по умолчанию',
  },
  localeFormats: {
    AUTO: 'Определять автоматически',
    DE: 'Немецкий',
    FR: 'Французский',
    INTERNATIONAL: 'Международный (ISO/US)',
  },
  duplicates: {
    SKIP: 'Пропустить существующую запись',
    UPDATE: 'Обновить существующую',
    CREATE_NEW: 'Создать новую запись',
    MANUAL: 'Решить вручную',
  },
  profileTitle: 'Повторно используемый профиль миграции',
  countryMapExample: 'Германия=DE',
  unitMapExample: 'Штука=PCE',
  taxMapExample: '19%=STANDARD',
  profileHint: 'Примените версионное сопоставление или сохраните проверенную конфигурацию.',
  profileReviewWarning:
    'Системные профили обозначают только источник. Формат сторонней системы не предполагается; проверьте каждое сопоставление.',
  profile: 'Профиль',
  sourceSystem: 'Исходная система',
  newProfileName: 'Имя нового профиля клиента',
  saveProfile: 'Сохранить сопоставление',
  updateProfile: 'Сохранить новую версию',
  archiveProfile: 'Архивировать профиль',
  profileSaved: 'Профиль импорта сохранён.',
  profileUpdated: 'Новая версия профиля сохранена.',
  profileArchived: 'Профиль импорта архивирован.',
  profiles: {
    generic: 'Обычный CSV/XLSX',
    orgamax: 'orgaMAX',
    odoo: 'Odoo',
    logoTiger: 'LOGO/Tiger',
  },
  openHistory: 'Открыть полную историю',
  historyDetails: 'Сведения об импорте',
  historyDetailsHint: 'Источник, параметры, затронутые записи, аудит и проверки отката.',
  mappingVersion: 'Версия сопоставления',
  dryRunAt: 'Тестовый запуск',
  auditEntries: 'Записи аудита',
  vaultEntries: 'Записи хранилища GoBD',
  mappingParameters: 'Точные параметры импорта',
  operation: 'Операция',
  affectedRecord: 'Затронутая запись',
  historyPage: 'Страница {{page}} из {{pages}} · строк: {{total}}',
  rollbackTitle: 'Безопасный откат',
  rollbackHint:
    'Удаляются только неизменённые записи, созданные исключительно этим импортом и не использованные далее.',
  rollbackPermissionRequired: 'Требуется специальное разрешение на откат.',
  checkRollback: 'Проверить возможность отката',
  correctionList: 'Скачать список исправлений',
  rollbackReason: 'Обязательная причина',
  rollbackConfirmation: 'Я проверил результат и явно подтверждаю перечисленные удаления.',
  executeRollback: 'Выполнить подтверждённый откат',
  rollbackCompleted: 'Импорт отменён с полным аудитом.',
  rollbackBlocked: 'Данные изменились; создан список исправлений.',
  rollbackStatuses: { ELIGIBLE: 'Разрешён', BLOCKED: 'Заблокирован', COMPLETED: 'Завершён' },
};
